import { useState } from 'react';
import { ClipboardCheck, X } from 'lucide-react';

import type { AuditDocument } from '@/hooks/useAudit';

/**
 * TankRentalAuditDoc — the merged "Tank Rental Audit" document rendered on a
 * CR-ADJ adjustment invoice in the portal (and reusable anywhere an audit id
 * is at hand). Same idea as the delivery confirmation: the invoice itself is
 * a plain $0 QBO invoice; this panel merges the driver's counts, photos and
 * signature into the document view.
 */

function fmtSignedAt(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
}

export function TankRentalAuditDoc({ audit }: { audit: AuditDocument }) {
  const [lightbox, setLightbox] = useState<string | null>(null);
  // The billed numbers: the manager's amended final_counts when the approval
  // changed them, else the driver's signed counts. The driver's original
  // shows alongside any amended row.
  const driverByLabel = new Map(audit.counts.map((c) => [c.label, c] as const));
  const rows = (audit.final_counts ?? audit.counts).map((c) => ({
    ...c,
    driver_counted: driverByLabel.get(c.label)?.counted ?? c.counted,
  }));
  const hasAmendments = rows.some((r) => r.counted !== r.driver_counted);
  const adjusted = rows.filter((c) => c.delta !== 0);

  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <ClipboardCheck className="h-4 w-4 text-brix-primary" />
        <span className="text-[10px] font-bold tracking-tight text-slate-900 uppercase">
          Cylinder audit
        </span>
        <span className="font-mono text-xs font-bold text-brix-primary">{audit.doc_number}</span>
      </div>

      {/* Counts — system vs counted vs adjustment */}
      <div className="overflow-hidden rounded-lg border border-brix-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-brix-surface-alt/60 text-left text-[11px] uppercase tracking-wide text-brix-muted">
              <th className="px-3 py-2 font-semibold">Cylinder</th>
              <th className="px-3 py-2 text-right font-semibold">System</th>
              <th className="px-3 py-2 text-right font-semibold">Counted</th>
              <th className="px-3 py-2 text-right font-semibold">Adjustment</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.label} className="border-t border-brix-border">
                <td className="px-3 py-2 font-semibold text-brix-ink">{c.label}</td>
                <td className="px-3 py-2 text-right font-mono text-brix-muted">{c.system_count}</td>
                <td className="px-3 py-2 text-right font-mono text-brix-ink">
                  {c.counted !== c.driver_counted ? (
                    <>
                      <span className="mr-1 text-brix-muted line-through">{c.driver_counted}</span>
                      {c.counted}
                    </>
                  ) : (
                    c.counted
                  )}
                </td>
                <td
                  className={`px-3 py-2 text-right font-mono font-bold ${
                    c.delta === 0
                      ? 'text-brix-muted'
                      : c.delta > 0
                        ? 'text-brix-success'
                        : 'text-brix-warning'
                  }`}
                >
                  {c.delta === 0 ? '—' : `${c.delta > 0 ? '+' : ''}${c.delta}`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {adjusted.length === 0 ? (
        <p className="mt-2 text-xs text-brix-muted">
          On-site count matched the system — recorded for the file, no balance change.
        </p>
      ) : null}
      {hasAmendments ? (
        <p className="mt-2 text-xs text-brix-muted">
          Struck numbers are the driver’s signed counts; the final counts were amended at
          approval{audit.approved_by_email ? ` by ${audit.approved_by_email}` : ''}.
        </p>
      ) : null}
      {audit.notes ? (
        <p className="mt-2 text-xs text-brix-muted">
          <span className="font-semibold text-brix-ink">Driver notes:</span> {audit.notes}
        </p>
      ) : null}

      {/* Photos */}
      {audit.photos.length > 0 ? (
        <div className="mt-4">
          <div className="mb-1.5 text-[10px] font-bold uppercase tracking-tight text-slate-900">
            Site photos · {audit.photos.length}
          </div>
          <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
            {audit.photos.map((p) => (
              <button
                key={p.path}
                type="button"
                onClick={() => setLightbox(p.url)}
                className="aspect-square overflow-hidden rounded-lg border border-brix-border transition-shadow hover:shadow-card-hover"
              >
                <img src={p.url} alt="Cylinder audit photo" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      ) : audit.photo_waived_by ? (
        <p className="mt-4 text-xs text-slate-600">
          No site photos — submitted without one by {audit.photo_waived_by} (manager/admin).
        </p>
      ) : null}

      {/* Signatures — driver, plus the optional on-site manager countersignature. */}
      <div className={`mt-4 grid gap-3 ${audit.manager_name || audit.manager_signature_url ? 'sm:grid-cols-2' : ''}`}>
        <div className="rounded-lg border border-brix-border bg-brix-surface-alt/40 p-4">
          <div className="text-[10px] font-bold uppercase tracking-tight text-slate-900">
            Verified on site
          </div>
          {audit.signature_url ? (
            <img
              src={audit.signature_url}
              alt={`Signature of ${audit.signed_name}`}
              className="mt-2 h-20 rounded-md border border-brix-border bg-white object-contain px-2"
            />
          ) : null}
          <div className="mt-2 text-sm font-semibold text-brix-ink">{audit.signed_name}</div>
          <div className="text-xs text-brix-muted">
            {audit.driver_name && audit.driver_name !== audit.signed_name
              ? `Driver: ${audit.driver_name} · `
              : ''}
            {fmtSignedAt(audit.signed_at)}
          </div>
        </div>

        {audit.manager_name || audit.manager_signature_url ? (
          <div className="rounded-lg border border-brix-border bg-brix-surface-alt/40 p-4">
            <div className="text-[10px] font-bold uppercase tracking-tight text-slate-900">
              Manager sign-off
            </div>
            {audit.manager_signature_url ? (
              <img
                src={audit.manager_signature_url}
                alt={`Signature of ${audit.manager_name ?? 'manager'}`}
                className="mt-2 h-20 rounded-md border border-brix-border bg-white object-contain px-2"
              />
            ) : (
              <div className="mt-2 flex h-20 items-center justify-center rounded-md border border-dashed border-brix-border text-xs text-brix-muted">
                name recorded, no signature
              </div>
            )}
            <div className="mt-2 text-sm font-semibold text-brix-ink">{audit.manager_name}</div>
            <div className="text-xs text-brix-muted">{fmtSignedAt(audit.signed_at)}</div>
          </div>
        ) : null}
      </div>

      {/* Approval record — shortage audits go through manager review. */}
      {audit.approved_at && audit.approved_by_email ? (
        <div className="mt-3 rounded-lg border border-brix-border bg-brix-surface-alt/40 px-4 py-3 text-xs text-brix-muted">
          <span className="font-semibold text-brix-ink">
            {audit.status === 'declined' ? 'Declined' : 'Approved'}
          </span>{' '}
          by {audit.approved_by_email} · {fmtSignedAt(audit.approved_at)}
          {audit.approval_notes ? <> — {audit.approval_notes}</> : null}
        </div>
      ) : null}

      {/* Lightbox */}
      {lightbox ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-brix-ink/80 p-4"
          onClick={() => setLightbox(null)}
        >
          <button
            type="button"
            aria-label="Close photo"
            className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white"
            onClick={() => setLightbox(null)}
          >
            <X className="h-5 w-5" />
          </button>
          <img
            src={lightbox}
            alt="Cylinder audit photo"
            className="max-h-full max-w-full rounded-xl object-contain"
          />
        </div>
      ) : null}
    </div>
  );
}
